import { useRecoilState, useSetRecoilState } from "recoil";
import { IUserProfile, userProfileState } from "@/store/user-profile";
import {
  getUserProfile,
  resetUserInitials,
  resetUserPassword,
  updateUserProfile,
  updateUserProfileImage,
} from "@/services/api-service/profiles/user-profile-api";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useSnackBar } from "@/hooks/use-snack-bar";
import {
  changeProfileImageState,
  changeProfileInitialsState,
} from "@/widgets/settings-profile-widget/state/change-profile-image";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { refreshAccessToken } from "@/services/refresh-token";
import { clearValidateSnapshot } from "@/services/auth/validate-cache";
import { getSessionPrintHouseId } from "@/services/auth/session-print-house-id";

const useUserProfile = () => {
  const [profileState, setProfileState] =
    useRecoilState<IUserProfile>(userProfileState);
  const { callApi } = useGomakeAxios();
  const { alertSuccessUpdate, alertFaultUpdate } = useSnackBar();
  const setChangeProfileImage = useSetRecoilState<boolean>(
    changeProfileImageState,
  );
  const [isSaving, setIsSaving] = useState(false);
  const setInitialsModal = useSetRecoilState(changeProfileInitialsState);
  const { i18n } = useTranslation();

  const getProfile = async () => {
    const callBack = (res) => {
      if (res.success) {
        setProfileState(res.data);
      }
    };

    await getUserProfile(callApi, callBack);
  };
  const updateProfile = async () => {
    setIsSaving(true);
    const previousLang = profileState.systemLang;
    const res = await updateUserProfile(callApi, () => {}, profileState);
    setIsSaving(false);

    if (res.success) {
      localStorage.setItem("systemLanguage", profileState.systemLang);
      i18n.changeLanguage(profileState.systemLang);

      try {
        await refreshAccessToken({
          printHouseId:
            profileState.printHouseId || getSessionPrintHouseId(),
        });
        clearValidateSnapshot();
      } catch {
        // Language saved in CRM; rebuild Redis session on next refresh/validate.
      }

      alertSuccessUpdate();
    } else {
      alertFaultUpdate();
    }
  };

  const changeUserProfileImage = async (file: any) => {
    const callBack = (res) => {
      if (res.success) {
        setProfileState({ ...profileState, imagePath: res.data?.url });
        setChangeProfileImage(false);
      }
    };
    const res = await updateUserProfileImage(callApi, callBack, {
      userId: profileState.id,
      fileBase64: file,
    });
    return res.success;
  };

  const updateUserInitials = async (data: {
    avatarBackGroundColor: string;
    avatarInitials: string;
  }) => {
    const callBack = (res) => {
      if (res.success) {
        setProfileState({ ...profileState, ...data, imagePath: "" });
        setInitialsModal(false);
        alertSuccessUpdate();
      } else {
        alertFaultUpdate();
      }
    };

    await resetUserInitials(callApi, callBack, {
      ...data,
      id: profileState.id,
    });
  };

  const updateUserPassword = async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    userId?: string,
    onError?: (message?: string) => void,
  ) => {
    const callBack = (res) => {
      if (res.success) {
        alertSuccessUpdate();
      } else {
        const message =
          res?.message || res?.data?.message || res?.errorMessage;
        if (onError) onError(message);
        alertFaultUpdate();
      }
    };
    const result = await resetUserPassword(callApi, callBack, {
      id: userId ? userId : profileState.id,
      currentPassword,
      newPassword,
      confirmPassword,
    });
    return result?.success ?? false;
  };
  return {
    updateProfile,
    getProfile,
    changeUserProfileImage,
    updateUserInitials,
    updateUserPassword,
    profileState,
    isSaving,
  };
};

export { useUserProfile };
